import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { digitalOcean } from "@/integrations/digitalocean/client";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Shield } from "lucide-react";

export default function PrivacyPolicyAcceptance() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [hasAccepted, setHasAccepted] = useState(false);

  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    const { data: { session } } = await digitalOcean.auth.getSession();
    if (!session) {
      navigate("/Auth");
      return;
    }
    const { data: profile } = await digitalOcean
      .from("profiles")
      .select("*")
      .eq("id", session.user.id)
      .single();
    setCurrentUser(profile);
    
    if (profile.has_accepted_privacy_policy) {
      navigate("/Discover");
    }
  };

  const acceptMutation = useMutation({
    mutationFn: async () => {
      const { error } = await digitalOcean
        .from("profiles")
        .update({ has_accepted_privacy_policy: true })
        .eq("id", currentUser.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Privacy Policy accepted");
      navigate("/Discover");
    },
    onError: () => {
      toast.error("Failed to accept Privacy Policy");
    },
  });

  const handleAccept = () => {
    if (!hasAccepted) {
      toast.error("Please check the box to accept the Privacy Policy");
      return;
    }
    acceptMutation.mutate();
  };

  if (!currentUser) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8 flex items-center justify-center">
      <div className="max-w-4xl w-full bg-card p-8 rounded-lg border border-border">
        <div className="flex items-center gap-3 mb-6">
          <Shield className="w-8 h-8 text-primary" />
          <h1 className="text-3xl font-bold text-card-foreground">Privacy Policy</h1>
        </div>

        <div className="bg-muted/50 p-6 rounded max-h-96 overflow-y-auto mb-6">
          <p className="text-sm text-foreground mb-4">
            Last Updated: {new Date().toLocaleDateString()}
          </p>
          
          <h2 className="text-lg font-semibold text-foreground mb-2">1. Information We Collect</h2>
          <p className="text-muted-foreground mb-4">
            We collect information you provide directly, including your name, email, username, and content you upload. We also collect usage data and device information.
          </p>

          <h2 className="text-lg font-semibold text-foreground mb-2">2. How We Use Your Information</h2>
          <p className="text-muted-foreground mb-4">
            We use your information to provide, maintain, and improve our services, communicate with you, and ensure platform security.
          </p>

          <h2 className="text-lg font-semibold text-foreground mb-2">3. Data Sharing</h2>
          <p className="text-muted-foreground mb-4">
            We do not sell your personal information. We may share data with service providers, for legal compliance, or with your consent.
          </p>

          <h2 className="text-lg font-semibold text-foreground mb-2">4. Data Security</h2>
          <p className="text-muted-foreground mb-4">
            We implement industry-standard security measures to protect your data. However, no method of transmission over the Internet is 100% secure.
          </p>

          <h2 className="text-lg font-semibold text-foreground mb-2">5. Your Rights</h2>
          <p className="text-muted-foreground mb-4">
            You have the right to access, update, or delete your personal information. Contact us to exercise these rights.
          </p>
        </div>

        <div className="flex items-center space-x-2 mb-6">
          <Checkbox 
            id="accept" 
            checked={hasAccepted}
            onCheckedChange={setHasAccepted}
          />
          <label
            htmlFor="accept"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            I have read and accept the Privacy Policy
          </label>
        </div>

        <div className="flex gap-4">
          <Button 
            onClick={handleAccept} 
            className="flex-1"
            disabled={!hasAccepted || acceptMutation.isPending}
            tooltip={!hasAccepted ? "Please check the box to accept" : acceptMutation.isPending ? "Accepting policy..." : "Accept privacy policy and continue"}
          >
            {acceptMutation.isPending ? "Accepting..." : "Accept and Continue"}
          </Button>
          <Button variant="outline" onClick={() => navigate("/PrivacyPolicy")} tooltip="View the full Privacy Policy document">
            Read Full Policy
          </Button>
        </div>
      </div>
    </div>
  );
}
